import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCustomerOrders } from '../../lib/customerApi';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ClipboardList, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const statusConfig = {
  pending: { label: '受付中', className: 'bg-amber-100 text-amber-800' },
  confirmed: { label: '確定', className: 'bg-blue-100 text-blue-800' },
  shipped: { label: '発送済', className: 'bg-purple-100 text-purple-800' },
  completed: { label: '完了', className: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'キャンセル', className: 'bg-red-100 text-red-800' },
};

export const CustomerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await getCustomerOrders();
      setOrders(res.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-orders">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">注文履歴</h1>
        <p className="text-slate-500 mt-1">過去のご注文を確認できます</p>
      </div>

      {orders.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ClipboardList className="h-16 w-16 text-slate-300 mb-4" />
            <p className="text-slate-500">注文履歴がありません</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const status = statusConfig[order.status] || statusConfig.pending;
            return (
              <Link key={order.id} to={`/customer/orders/${order.id}`}>
                <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer" data-testid={`order-card-${order.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-medium text-blue-600">{order.order_number}</span>
                          <Badge className={status.className}>{status.label}</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span>
                            {format(new Date(order.created_at), 'yyyy年MM月dd日 HH:mm', { locale: ja })}
                          </span>
                          <span>{order.items?.length || 0}点</span>
                          <span className="font-mono font-medium text-slate-900">
                            ¥{order.total_amount.toLocaleString()}
                          </span>
                        </div>
                        {order.notes && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-1">
                            メモ: {order.notes}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
